import React, { useRef, useState } from 'react';
import FlipCard, { FlipCardFront, FlipCardBack } from '../../../FlipCard/FlipCard';
import AccountActivity from '../../../Dashboard/AccountActivity/AccountActivity';
import Withdrawal from '../../../Dashboard/Account/Withdrawal';
import Deposit from '../../../Dashboard/Account/Deposit';
import Button from '../../../Form/Button/Button';
import AccountCardFront from './AccountCardFront';
import { useAccountActionButton } from './AccountCardActions';
import { useUnifiedAnalytics } from '../../../../contexts/AnalyticsWrapper';
import NoticeAccountClosureModal from '../../../MyAccount/AccountClosure/NoticeAccountClosureModal';
import { productTypes } from '../../../../utils/productHelpers'; 

/**
 * Account Card component with flip functionality
 * @param {{ 
 *   account: any;
 *   customer: any;
 *   toAccount?: any;
 *   isFlipped: boolean;
 *   actionType: 'withdraw' | 'withdraw-confirm' | 'deposit' | null;
 *   activityIsShowing: boolean;
 *   onToggleActivity: () => void;
 *   onStartWithdrawal: () => void;
 *   onStartDeposit: () => void;
 *   onStartTransfer: () => void;
 *   onUnflipCard: () => void;
 *   onAfterUnflip: () => void;
 *   onInfoIconClick: (event: React.MouseEvent, accountId: string) => void;
 *   onMaturityInfoIconClick: (event: React.MouseEvent, accountId: string) => void;
 *   onAvailableBalanceIconClick: (event: React.MouseEvent, accountId: string) => void;
 *   onFundingInfoIconClick: (event: React.MouseEvent, accountId: string) => void;
 *   onRateChangeInfoIconClick: (event: React.MouseEvent, accountId: string) => void;
 *   openTooltipId: string | null;
 *   openMaturityTooltipId: string | null;
 *   openFundingTooltipId: string | null;
 *   openRateChangeTooltipId: string | null;
 *   setActionType: (type: 'withdraw' | 'deposit' | null) => void;
 * }} props
 */
const AccountCard = ({
    account,
    customer,
    hasDisaAccountAgainstFisa,
    toAccount,
    isFlipped,
    actionType,
    activityIsShowing,
    onToggleActivity,
    onStartWithdrawal,
    onStartDeposit,
    onStartTransfer,
    onUnflipCard,
    onAfterUnflip,
    onInfoIconClick,
    onMaturityInfoIconClick,
    onAvailableBalanceIconClick,
    onFundingInfoIconClick,
    onRateChangeInfoIconClick,
    openTooltipId,
    openMaturityTooltipId,
    openFundingTooltipId,
    openRateChangeTooltipId,
    setActionType
}) => {
    const activityRef = useRef(null);
    const isClosed = account.accountStatus === 'Closed' || account.accountStatus === 'ClosureRequested';
    const isClosureRequested = account.accountStatus === 'ClosureRequested';

    const [showClosure, setShowClosure] = useState(false);
    const [selectedAccountNumber, setSelectedAccountNumber] = useState('');

    const getDestinationAccounts = () => {
        /** @type {import('../../../Dashboard/DashboardTypes').TransferAccount[]} */
        const transferAccounts = account.transferAccounts;
        /** @type {import('../../../MyAccount/MyAccountTypes').DestinationAccount[]} */
        const mappedAccounts = transferAccounts.map(function (transferAccount) {
            return {
                accountNumber: transferAccount.accountNumber,
                sortCode: transferAccount.sortCode,
                productName: transferAccount.product.name,
                label: `${transferAccount.product.name} - ${transferAccount.accountNumber}`,
                productType: /** @type {string | null} */ (transferAccount.product.productType),
                balance: /** @type {number | null} */ (transferAccount.balance),
                isExternalAccount: false
            };
        });
        mappedAccounts.push({
            accountNumber: account.linkedAccountNumber || '',
            sortCode: account.linkedAccountSortCode || '',
            productName: 'Linked Account',
            label: `Linked Account - ${account.linkedAccountNumber}`,
            productType: null,
            balance: null,
            isExternalAccount: true
        });
        return mappedAccounts;
    };

    const onCloseAccount = (/** @type {string} */ accountNumber) => {
        setShowClosure(true);
        setSelectedAccountNumber(accountNumber);
    };

    const onAccountClosed = () => {
        account.availableBalance = 0;
        setShowClosure(false);
        onUnflipCard();
    };

    const onAccountClosureCancelled = () => {
        setShowClosure(false);
    };
    
    const actionButton = useAccountActionButton({
        account,
        isClosed,
        onStartWithdrawal,
        onStartDeposit,
        onStartTransfer
    });

    const analytics = useUnifiedAnalytics();

    const renderAccountBack = () => {
        if (actionType === 'deposit') {
            return (
                <Deposit 
                    account={account} 
                    toAccount={toAccount} 
                    onCancel={onUnflipCard} 
                    onDone={onUnflipCard} 
                />
            );
        }
        if (actionType === 'withdraw-confirm') {
            const availableBalance = Number.isFinite(Number(account.availableBalance))
                ? Number(account.availableBalance)
                : Number(account.balance) || 0;

            return (
                <div className="account-card__withdraw-confirmation">
                    <div className="account-card__withdraw-confirmation-source">
                        <h3>Withdrawal</h3>
                        <div className="account-card__withdraw-confirmation-row account-card__withdraw-confirmation-row--from">
                            <span>From</span>
                            <strong>{account.product.name}</strong>
                        </div>
                        <div className="account-card__withdraw-confirmation-row">
                            <span>Available balance</span>
                            <strong>£{availableBalance.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong>
                        </div>
                    </div>
                    <div className="account-card__withdraw-confirmation-copy">
                        <p className="account-card__withdraw-confirmation-title">You&apos;re about to withdraw money from your ISA</p>
                        <p>Once your money leaves your ISA, it will lose its tax-free status. This can&apos;t be undone.</p>
                    </div>
                    <div className="account-card__withdraw-confirmation-actions">
                        <Button type="button" size="med" label="Withdraw from your ISA" onClick={() => {
                            if (analytics) {
                                analytics.trackAnalyticsClickEvent(
                                    'withdraw from your isa confirmation',
                                    '',
                                    'dashboard account card confirmation',
                                    account.product.code,
                                    'isa withdrawal confirm',
                                    {},
                                    'button click',
                                    'dashboard|click|withdraw-isa-confirm',
                                    'dashboard'
                                );
                            }
                            setActionType('withdraw');
                        }} />
                        <Button type="button" size="med" variant="secondary" label="Cancel" onClick={() => {
                            if (analytics) {
                                analytics.trackAnalyticsClickEvent(
                                    'cancel isa withdrawal',
                                    '',
                                    'dashboard account card confirmation',
                                    account.product.code,
                                    'isa withdrawal cancel',
                                    {},
                                    'button click',
                                    'dashboard|click|cancel-isa-withdrawal',
                                    'dashboard'
                                );
                            }
                            onUnflipCard();
                        }} />
                    </div>
                </div>
            );
        }
        if (actionType === 'withdraw') {
            return (
                <Withdrawal
                    account={account}
                    toAccount={toAccount}
                    customer={customer}
                    onCancel={onUnflipCard}
                    onDone={onUnflipCard}
                    noticePeriod={account.notice ? {
                        unit: account.notice.noticePeriodUnit,
                        length: account.notice.noticePeriod
                    } : null}
                    enableClosure={account.product.productType === productTypes.Notice}
                    onCloseAccount={onCloseAccount}
                />
            );
        }
        return null;
    };

    return (
        <div className="account-slide">
            <FlipCard 
                flipped={isFlipped} 
                onAfterFlip={(flipped) => { if (!flipped) onAfterUnflip(); }}
            >
                <FlipCardFront>
                    <AccountCardFront
                        account={account}
                        customer={customer}
                        hasDisaAccountAgainstFisa={hasDisaAccountAgainstFisa}
                        isClosed={isClosed}
                        isClosureRequested={isClosureRequested}
                        activityIsShowing={activityIsShowing}
                        actionButton={actionButton}
                        onToggleActivity={onToggleActivity}
                        onInfoIconClick={onInfoIconClick}
                        onMaturityInfoIconClick={onMaturityInfoIconClick}
                        onAvailableBalanceIconClick={onAvailableBalanceIconClick}
                        onFundingInfoIconClick={onFundingInfoIconClick}
                        onRateChangeInfoIconClick={onRateChangeInfoIconClick}
                        openTooltipId={openTooltipId}
                        openMaturityTooltipId={openMaturityTooltipId}
                        openFundingTooltipId={openFundingTooltipId}
                        openRateChangeTooltipId={openRateChangeTooltipId}
                    />
                </FlipCardFront>
                <FlipCardBack>
                    {renderAccountBack()}
                </FlipCardBack>
            </FlipCard>
            
            {activityIsShowing && (
                <AccountActivity 
                    activityRef={activityRef} 
                    account={account}
                    disable={false}
                />
            )}
            {showClosure && (
                <NoticeAccountClosureModal
                    productName={account.product.name}
                    mobileNumber={customer.mobileNumber}
                    accountId={account.id}
                    accountStatus={account.accountStatus}
                    selectedAccountNumber={selectedAccountNumber}
                    destinationAccounts={getDestinationAccounts()}
                    onClosed={onAccountClosed}
                    onCancel={onAccountClosureCancelled}
                />
            )}
        </div>
    );
};

export default AccountCard;
